import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import AccessibilityChip from "./AccessibilityChip";
import MediaPreViewModal from "@/common/MediaPreviewModal";
import { Place, ApiMedia, MediaSection } from "@/types/common";
import {
  AccessTimeFilledRounded,
  CheckCircleOutlineOutlined,
} from "@mui/icons-material";
import { getPlaceOpeningHours, getMediaItems } from "@/utils/helper";
import ActionButtonGroup from "./ActionButtonGroup";
import PlaceThumbnail from "@/shared/components/PlaceThumbnail";
import useMediaPreview from "@/hooks/useMediaPreview";

interface PlaceCardProps {
  place: Place | null;
  isCardView?: boolean;
  onClick?: (place: Place | null) => void;
  onClose?: () => void;
  enableMediaPreview?: boolean;
}

const styles = {
  card: {
    mb: "1%",
    bgcolor: "#fff",
    cursor: "pointer",
  },
  cardHeader: { display: "flex", justifyContent: "space-between", mb: "2%" },
  placeName: {
    color: "#191919",
    fontSize: { xs: "4.85vw", sm: 20 },
    fontWeight: 700,
  },
  placeAddress: {
    color: "#626262",
    fontSize: { xs: "2.91vw", sm: 12 },
    mt: "1.2%",
    mb: "2.18%",
  },
  accessibilityContainer: {
    display: "flex",
    gap: "2%",
    alignItems: "center",
    width: "100%",
  },
  statusContainer: {
    display: "flex",
    alignItems: "center",
    gap: "1%",
    color: "#666",
    width: "50%",
  },
  statusIcon: { fontSize: 16 },
  dot: { color: "#C4C4C4", fontSize: { xs: "2.91vw", sm: 12 } },
  statusText: {
    fontWeight: "bold",
    fontSize: { xs: "2.91vw", sm: 12 },
  },
  detailText: {
    color: "#626262",
    fontSize: { xs: "2.91vw", sm: 12 },
  },
  contributionsContainer: { mt: "3%", mb: "4%" },
  pendingBox: {
    backgroundColor: "#fff3e0",
    p: "2%",
    borderRadius: 1,
    border: "1px solid #ffb74d",
  },
  pendingText: {
    fontSize: { xs: "3.4vw", sm: 14 },
    color: "#e65100",
    fontWeight: 500,
  },
  availableBox: { display: "flex", alignItems: "center", gap: "1vw" },
  checkIcon: { color: "#74CE6A", fontSize: { xs: "4.45vw", sm: 18 } },
  availableText: {
    fontSize: { xs: "3.4vw", sm: 14 },
    color: "#74CE6A",
    fontWeight: 700,
  },
  sectionTitle: {
    mb: "0.5%",
    fontWeight: 700,
    fontSize: { xs: "3.4vw", sm: 14 },
    color: "#191919",
  },
  mediaList: {
    display: "flex",
    gap: "2%",
    mb: "4%",
    overflowX: "auto",
    pb: "1%",
    "&::-webkit-scrollbar": { display: "none" },
    scrollbarWidth: "none",
  },
  mediaItem: {
    width: { xs: "19.42vw", sm: "25%" },
    height: { xs: "19.42vw", sm: "25%" },
    aspectRatio: "1/1",
    borderRadius: "12px",
    overflow: "hidden",
    position: "relative",
    bgcolor: "#f0f0f0",
    flexShrink: 0,
    cursor: "pointer",
  },
  videoWrapper: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    bgcolor: "#000",
  },
  playIcon: { color: "#fff", fontSize: 32 },
  videoElement: {
    position: "absolute" as const,
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover" as const,
    opacity: 0.6,
  },
  imageElement: {
    width: "100%",
    height: "100%",
    objectFit: "cover" as const,
  },
  noMediaText: { color: "#626262", fontSize: { xs: "3.16vw", sm: 13 } },
};

const PlaceCard: React.FC<PlaceCardProps> = ({
  place,
  isCardView = false,
  onClick,
  onClose,
  enableMediaPreview = false,
}) => {
  const {
    name,
    address_text,
    opening_hours,
    contributions,
    has_pending_contribution,
  } = place || {};

  const { media: flatMedia, accessibility, buildings } = contributions?.[0] ?? {};

  const allMedia = useMemo((): (MediaSection | ApiMedia)[] => {
    const items: (MediaSection | ApiMedia)[] = [...(flatMedia || [])];
    (buildings || []).forEach((building) => {
      (building.entryPoints || []).forEach((ep) => {
        (ep.media || []).forEach((m) => items.push(m));
      });
    });
    return items;
  }, [flatMedia, buildings]);

  const mediaItems = useMemo(() => getMediaItems(allMedia, true), [allMedia]);
  const { handleMediaClick: onMediaClick, selectedMedia, selectedPhoto, selectedVideo, setSelectedPhoto, setSelectedVideo } = useMediaPreview(mediaItems);

  const { isOpen, statusText, detailText } =
    getPlaceOpeningHours(opening_hours);
  const statusColor = isOpen ? "#4caf50" : "#d32f2f";

  return (
    <>
      <Box
        sx={{
          ...styles.card,
          p: isCardView ? "4%" : "0 4%",
          boxShadow: isCardView ? "0px 4px 20px rgba(0,0,0,0.05)" : "none",
        }}
        onClick={() => onClick && onClick(place)}
      >
        <Box sx={styles.cardHeader}>
          <Box>
            <Typography variant="h6" sx={styles.placeName}>
              {name}
            </Typography>
            <Typography variant="body2" sx={styles.placeAddress}>
              {address_text}
            </Typography>
          </Box>
        </Box>

        <Box sx={styles.accessibilityContainer}>
          <AccessibilityChip status={accessibility || "no_data"} />

          <Box sx={styles.statusContainer}>
            {statusText != "Unknown" && (
              <>
                <Typography variant="caption" sx={styles.dot}>
                  •
                </Typography>
                <AccessTimeFilledRounded
                  sx={{ ...styles.statusIcon, color: statusColor }}
                />
                <Typography
                  variant="caption"
                  sx={{ ...styles.statusText, color: statusColor }}
                >
                  {statusText}
                </Typography>
              </>
            )}
            {detailText && (
              <>
                <Typography variant="caption" sx={styles.dot}>
                  •
                </Typography>
                <Typography variant="caption" sx={styles.detailText}>
                  {detailText}
                </Typography>
              </>
            )}
          </Box>
        </Box>

        <Box sx={styles.contributionsContainer}>
          {has_pending_contribution ? (
            <Box sx={styles.pendingBox}>
              <Typography sx={styles.pendingText}>
                Your contribution to this place is under review. You can make
                changes from your profile by editing it.
              </Typography>
            </Box>
          ) : (
            <Box sx={styles.availableBox}>
              <CheckCircleOutlineOutlined sx={styles.checkIcon} />
              <Typography sx={styles.availableText}>
                You can contribute to this place
              </Typography>
            </Box>
          )}
        </Box>
        {!isCardView && (
          <Typography variant="subtitle2" sx={styles.sectionTitle}>
            Photos & Videos
          </Typography>
        )}
        {allMedia?.length ? (
          <Box sx={styles.mediaList}>
            {mediaItems.map(
              (item, index: number) => (
                    <Box
                      key={index}
                      onClick={(e) => {
                        // Only stop propagation in detail view (when media preview is enabled)
                        if (enableMediaPreview) {
                          e.stopPropagation();
                        }
                      }}
                      sx={styles.mediaItem}
                    >
                      {item.type === "video" ? (
                        <Box
                          sx={styles.videoWrapper}
                          onClick={(e) => {
                            if (enableMediaPreview) {
                              e.stopPropagation();
                              onMediaClick("video", item.url);
                            }
                          }}
                        >
                          <PlayCircleOutlineIcon sx={styles.playIcon} />
                          {item.url && (
                            <video src={item.url} style={styles.videoElement} />
                          )}
                        </Box>
                      ) : (
                        <PlaceThumbnail
                          onClick={(e: React.MouseEvent<HTMLImageElement>) => {
                            if (enableMediaPreview) {
                              e.stopPropagation();
                              onMediaClick("image", item.url);
                            }
                          }}
                          src={item.url}
                          alt="place"
                          sx={{ ...styles.imageElement, border: "none" }}
                        />
                      )}
                    </Box>
                  ),
              )}
          </Box>
        ) : !isCardView ? (
          <Typography variant="caption" sx={styles.noMediaText}>
            No photos or videos available yet. Be the first to contribute media
            for this location!
          </Typography>
        ) : (
          ""
        )}

        {isCardView && place && (
          <ActionButtonGroup place={place} onClose={onClose} />
        )}
      </Box>
      {enableMediaPreview && (
        <MediaPreViewModal
          selectedPhoto={selectedPhoto}
          selectedVideo={selectedVideo}
          setSelectedPhoto={setSelectedPhoto}
          setSelectedVideo={setSelectedVideo}
          allMedia={selectedMedia}
        />
      )}
    </>
  );
};

export default PlaceCard;
